import { useEffect, useMemo, useState, startTransition } from 'react'
import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { getCookie, setCookie } from 'cookies-next'
import { REGION_COOKIE } from '@/constants/cookies'
import { Durations } from '@/constants/durations'
import { CANADA, DOMESTIC_REGIONS, UNITED_STATES, UNITED_STATES_AND_TERRITORIES } from '@/constants/regions'
import { GetViewerRegionQuery } from '@/types/codegen-federation'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { isDevelopmentEnvironment } from '@/utils/environment-utils'
import { Maybe } from '@/utils/types'

const getViewerRegionQuery = gql`
  query getViewerRegion {
    viewer {
      region
    }
  }
`

interface UseUserRegionResult {
  loading: boolean
  region: string | undefined
}

/**
 * This hook is for use client-side only.
 * @returns
 */
export function useUserRegion(): UseUserRegionResult {
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    startTransition(() => {
      setMounted(true)
    })
  }, [])

  const result = useQuery<GetViewerRegionQuery>(getViewerRegionQuery, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  })

  return useMemo(() => {
    if (!mounted) {
      return { loading: true, region: undefined }
    }
    const hydraRegion = result?.data?.viewer?.region
    const cookieRegion = getRegionFromCookie()
    const region = overrideOrRegion(hydraRegion || cookieRegion)

    if (hydraRegion) {
      setCookie(REGION_COOKIE, hydraRegion, { maxAge: Durations.ONE_MONTH_IN_SECONDS })
    }

    return { loading: result.loading, region }
  }, [result, mounted])
}

export function getUserRegionFromReq(req: { headers: Record<string, string | string[] | undefined> }): string | null {
  const vercelRegion = getItemOrFirstEntry(req.headers['x-vercel-ip-country'])
  return overrideOrRegion(vercelRegion) ?? null
}

/**
 * Returns the user's state/region code (e.g. "CA", "GA") from Vercel's geo headers.
 * This reads the `x-vercel-ip-country-region` header which is only available on the server.
 * Do not use client side.
 */
export function getUserStateFromReq(req: { headers: Record<string, string | string[] | undefined> }): string | null {
  const state = getItemOrFirstEntry(req.headers['x-vercel-ip-country-region'])
  return state ? state.toUpperCase() : null
}

export function overrideOrRegion(region: Maybe<string>): string | undefined {
  const mockRegion = process.env.NEXT_PUBLIC_MOCK_REGION?.toUpperCase()
  if (isDevelopmentEnvironment() && mockRegion) return mockRegion
  return region?.toUpperCase()
}

export function isUnitedStates(region: Maybe<string>): boolean {
  if (!region) return false
  const upper = region.toUpperCase()
  return Boolean(upper === UNITED_STATES)
}

export function isCanada(region: Maybe<string>): boolean {
  if (!region) return false
  const upper = region.toUpperCase()
  return Boolean(upper === CANADA)
}

export function isUnitedStatesOrCanada(region: Maybe<string>): boolean {
  if (!region) return false
  const upper = region.toUpperCase()
  return Boolean(upper === UNITED_STATES || upper === CANADA)
}

const US_AND_TERRITORIES = new Set(UNITED_STATES_AND_TERRITORIES)
export function isUnitedStatesOrTerritories(region: Maybe<string>): boolean {
  if (!region) return false
  const upper = region.toUpperCase()
  return US_AND_TERRITORIES.has(upper)
}

const DOMESTIC = new Set(DOMESTIC_REGIONS)
export function isDomestic(region: Maybe<string>): boolean {
  if (!region) return false
  const upper = region.toUpperCase()
  return DOMESTIC.has(upper)
}

/**
 * NOTE - Only use this function client side. If used server side, the cookie will always be one request behind.
 * Use getUserRegionFromReq server side.
 * @returns
 */
export function getRegionFromCookie(): string | undefined {
  const region = getCookie(REGION_COOKIE)
  return typeof region === 'string' ? region : undefined
}
